import React from "react";
import { action, computed, observable, makeObservable, runInAction } from "mobx";
import { MrbBaseViewStore } from "mrb/core";
import { SubPodcastAnalyticsStatsWidgetViewStore } from "administration/podcast/stores";
import { applicationErrorCodes } from "common/constants";
import { some } from "lodash";
import { localizationService } from "common/localization";
import { femaleContentLockedFields } from "common/utils";

class PodcastGroupPreviewSubPodcastViewStore extends MrbBaseViewStore {
    @observable isCollapsed = false;
    @observable podcast = null;
    panelRef = React.createRef();

    @computed get coverImageUrl() {
        if (this.podcast && this.podcast.coverImageId) {
            return this.routeStore.createPodcastCoverImageUrl(this.podcast.coverImageId, 150, 150);
        }

        return null;
    }

    @computed get isPremiumDisabled() {
        return this.podcastGroupPreviewViewStore.item.isPremium;
    }

    @computed get isAppVisibilityDisabled() {
        return !this.podcastGroupPreviewViewStore.item.isVisibleInApp;
    }

    @computed get isNakiTouchVisibilityDisabled() {
        return !this.podcastGroupPreviewViewStore.item.isVisibleInEmbeddedApp;
    }

    @computed get isHandheldPinkVisibilityDisabled() {
        return !this.podcastGroupPreviewViewStore.item.isVisibleInHandheldPinkApp;
    }

    @computed get isHandheldVisibilityDisabled() {
        return !this.podcastGroupPreviewViewStore.item.isVisibleInHandheldApp;
    }

    // A sub-podcast carries its own IsFemale. Turning it on at the group level cascades down, but a
    // group with the flag off may still have individual sub-podcasts marked as female content.
    @computed get isFemaleContent() {
        return this.podcast?.isFemale === true;
    }

    @computed get showScheduledRelease() {
        return !this.podcast.isActive;
    }

    constructor(rootStore, { podcastGroupPreviewViewStore, podcast }) {
        super(rootStore);
        makeObservable(this);
        this.podcastGroupPreviewViewStore = podcastGroupPreviewViewStore;
        this.routeStore = podcastGroupPreviewViewStore.routeStore;
        this.podcast = podcast;

        this.subPodcastAnalyticsStatsWidgetViewStore = new SubPodcastAnalyticsStatsWidgetViewStore(rootStore, {
            routeStore: this.routeStore,
            podcastId: this.podcast.id,
        });

        this.reaction(
            () => this.isCollapsed,
            (isCollapsed) => {
                if (isCollapsed) {
                    this.subPodcastAnalyticsStatsWidgetViewStore.initialize();
                    if (this.panelRef) {
                        // We need to execute scroll function asynchronously in order to take effect.
                        setTimeout(() => {
                            this.panelRef.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                            });
                        });
                    }
                }
            }
        );

        this.reaction(
            () => this.podcastGroupPreviewViewStore.item.isPremium,
            (isPodcastGroupPremium) => {
                if (isPodcastGroupPremium && !this.podcastGroupPreviewViewStore.isPremiumDisabled) {
                    runInAction(() => {
                        this.podcast.isPremium = true;
                    });
                }
            }
        );

        this.reaction(
            () => this.podcastGroupPreviewViewStore.item.isVisibleInApp,
            (isVisibleInApp) => {
                runInAction(() => {
                    this.podcast.isVisibleInApp = isVisibleInApp;
                });
            }
        );

        this.reaction(
            () => this.podcastGroupPreviewViewStore.item.isVisibleInEmbeddedApp,
            (isVisibleInEmbeddedApp) => {
                runInAction(() => {
                    this.podcast.isVisibleInEmbeddedApp = isVisibleInEmbeddedApp;
                });
            }
        );

        this.reaction(
            () => this.podcast.isVisibleInApp,
            (isVisibleInApp) => {
                if (!isVisibleInApp) {
                    if (
                        !some(
                            this.podcastGroupPreviewViewStore.item.subPodcasts,
                            (subPodcast) => subPodcast.isVisibleInApp
                        )
                    ) {
                        runInAction(() => {
                            this.podcastGroupPreviewViewStore.item.isVisibleInApp = false;
                        });
                    }
                }
            }
        );

        this.reaction(
            () => this.podcast.isVisibleInEmbeddedApp,
            (isVisibleInEmbeddedApp) => {
                if (!isVisibleInEmbeddedApp) {
                    if (
                        !some(
                            this.podcastGroupPreviewViewStore.item.subPodcasts,
                            (subPodcast) => subPodcast.isVisibleInEmbeddedApp
                        )
                    ) {
                        runInAction(() => {
                            this.podcastGroupPreviewViewStore.item.isVisibleInEmbeddedApp = false;
                        });
                    }
                }
            }
        );
    }

    @action.bound
    onInit() {
        const subPodcastId = this.rootStore.routerStore.routerState.queryParams.subPodcastId;
        if (subPodcastId && this.podcast.id === subPodcastId) {
            this.isCollapsed = true;
        }
        return Promise.resolve();
    }

    @action.bound
    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
    }

    @action.bound
    async toggleIsActive(value) {
        const oldIsActive = this.podcast.isActive;
        const oldScheduledReleaseAt = this.podcast.scheduledReleaseAt;
        this.podcast.isActive = value;
        try {
            if (value && this.podcast.scheduledReleaseAt) {
                this.podcast.scheduledReleaseAt = null;
                await this.routeStore.updatePodcastScheduledReleaseAt(this.podcast.id, null);
            }
            await this.routeStore.updateActiveStatus(this.podcast.id, value);
        } catch (err) {
            if (err.data.errorCode === applicationErrorCodes.rssSync.rssSyncFailed) {
                this.rootStore.notificationStore.error(
                    "Podcast updated, but an unexpected error occurred during RSS sync.",
                    err
                );
            } else {
                runInAction(() => {
                    this.podcast.isActive = oldIsActive;
                    this.podcast.scheduledReleaseAt = oldScheduledReleaseAt;
                    this.rootStore.notificationStore.error("Unexpected error occurred. Unable to change status", err);
                });
            }
        }
    }

    @action.bound
    async togglePremiumStatus(value) {
        const oldValue = this.podcast.isPremium;
        this.podcast.isPremium = value;
        try {
            await this.routeStore.updatePremiumStatus(this.podcast.id, this.podcast.isPremium);
        } catch (err) {
            if (this.podcast.isPremium && this.podcast.producerId === null) {
                runInAction(() => {
                    this.podcast.isPremium = oldValue;
                    this.rootStore.notificationStore.warning(
                        localizationService.t("ADMINISTRATION.PODCAST.LIST.VALIDATION.PODCAST_MISSING_PRODUCER")
                    );
                });
            } else {
                runInAction(() => {
                    this.podcast.isPremium = oldValue;
                    this.rootStore.notificationStore.error("Unexpected error occurred. Unable to change premium", err);
                });
            }
        }
    }

    @action.bound
    async toggleOnByDefaultStatus(value) {
        const oldValue = this.podcast.isTurnedOnByDefault;
        this.podcast.isTurnedOnByDefault = value;
        try {
            await this.routeStore.updateOnByDefaultStatus(this.podcast.id, this.podcast.isTurnedOnByDefault);
        } catch (err) {
            runInAction(() => {
                this.podcast.isTurnedOnByDefault = oldValue;
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Unable to change default status",
                    err
                );
            });
        }
    }

    @action.bound
    async toggleOnByDefaultInAppStatus(value) {
        const oldValue = this.podcast.isTurnedOnByDefaultInApp;
        this.podcast.isTurnedOnByDefaultInApp = value;
        try {
            await this.routeStore.updateOnByDefaultInAppStatus(this.podcast.id, this.podcast.isTurnedOnByDefaultInApp);
        } catch (err) {
            runInAction(() => {
                this.podcast.isTurnedOnByDefaultInApp = oldValue;
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Unable to change default status",
                    err
                );
            });
        }
    }

    @action.bound
    async toggleExcludedFromRevenueStatus(value) {
        const oldValue = this.podcast.excludedFromRevenue;
        this.podcast.excludedFromRevenue = value;
        try {
            await this.routeStore.updateExcludedFromRevenueStatus(this.podcast.id, this.podcast.excludedFromRevenue);
        } catch (err) {
            if (err.data?.errorCode === applicationErrorCodes.rssSync.rssSyncFailed) {
                this.rootStore.notificationStore.error(
                    "Podcast updated, but an unexpected error occurred during RSS sync.",
                    err
                );
            } else {
                runInAction(() => {
                    this.podcast.excludedFromRevenue = oldValue;
                    this.rootStore.notificationStore.error("Unexpected error occurred. Unable to change status", err);
                });
            }
        }
    }

    @action.bound
    async toggleOnByDefaultInNakiTouchStatus(value) {
        const oldValue = this.podcast.isTurnedOnByDefaultInEmbeddedApp;
        this.podcast.isTurnedOnByDefaultInEmbeddedApp = value;
        try {
            await this.routeStore.updateOnByDefaultInNakiTouchStatus(
                this.podcast.id,
                this.podcast.isTurnedOnByDefaultInEmbeddedApp
            );
        } catch (err) {
            runInAction(() => {
                this.podcast.isTurnedOnByDefaultInEmbeddedApp = oldValue;
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Unable to change default status",
                    err
                );
            });
        }
    }

    @action.bound
    async toggleOnByDefaultInHandheldPinkStatus(value) {
        const oldValue = this.podcast.isTurnedOnByDefaultInHandheldPinkApp;
        this.podcast.isTurnedOnByDefaultInHandheldPinkApp = value;
        try {
            await this.routeStore.updateOnByDefaultInHandheldPinkStatus(
                this.podcast.id,
                this.podcast.isTurnedOnByDefaultInHandheldPinkApp
            );
        } catch (err) {
            runInAction(() => {
                this.podcast.isTurnedOnByDefaultInHandheldPinkApp = oldValue;
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Unable to change default status",
                    err
                );
            });
        }
    }

    @action.bound
    async toggleOnByDefaultInHandheldStatus(value) {
        const oldValue = this.podcast.isTurnedOnByDefaultInHandheldApp;
        this.podcast.isTurnedOnByDefaultInHandheldApp = value;
        try {
            await this.routeStore.updateOnByDefaultInHandheldStatus(
                this.podcast.id,
                this.podcast.isTurnedOnByDefaultInHandheldApp
            );
        } catch (err) {
            runInAction(() => {
                this.podcast.isTurnedOnByDefaultInHandheldApp = oldValue;
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Unable to change default status",
                    err
                );
            });
        }
    }

    @action.bound
    async toggleRecentlyUpdatedVisibilityStatus(value) {
        const oldValue = this.podcast.podcastFeed.recentlyUpdatedVisibility;
        this.podcast.podcastFeed.recentlyUpdatedVisibility = value;
        try {
            await this.routeStore.updateRecentlyUpdatedVisibilityStatus(
                this.podcast.id,
                this.podcast.podcastFeed.recentlyUpdatedVisibility
            );
        } catch (err) {
            runInAction(() => {
                this.podcast.podcastFeed.recentlyUpdatedVisibility = oldValue;
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Unable to change visibility status",
                    err
                );
            });
        }
    }

    @action.bound
    async toggleAppVisibilityStatus(value) {
        const oldValue = this.podcast.isVisibleInApp;
        this.podcast.isVisibleInApp = value;
        try {
            await this.routeStore.updateAppVisibilityStatus(this.podcast.id, this.podcast.isVisibleInApp);
        } catch (err) {
            runInAction(() => {
                this.podcast.isVisibleInApp = oldValue;
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Unable to change app visibility status",
                    err
                );
            });
        }
    }

    @action.bound
    async toggleNakiTouchVisibilityStatus(value) {
        const oldValue = this.podcast.isVisibleInEmbeddedApp;
        this.podcast.isVisibleInEmbeddedApp = value;
        try {
            await this.routeStore.updateNakiTouchVisibilityStatus(this.podcast.id, this.podcast.isVisibleInEmbeddedApp);
        } catch (err) {
            runInAction(() => {
                this.podcast.isVisibleInEmbeddedApp = oldValue;
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Unable to change Naki Bolt visibility status",
                    err
                );
            });
        }
    }

    @action.bound
    async toggleHandheldPinkVisibilityStatus(value) {
        const oldValue = this.podcast.isVisibleInHandheldPinkApp;
        this.podcast.isVisibleInHandheldPinkApp = value;
        try {
            await this.routeStore.updateHandheldPinkVisibilityStatus(this.podcast.id, this.podcast.isVisibleInHandheldPinkApp);
        } catch (err) {
            runInAction(() => {
                this.podcast.isVisibleInHandheldPinkApp = oldValue;
                this.rootStore.notificationStore.error(
                    localizationService.t("ADMINISTRATION.PODCAST.PREVIEW.HANDHELD_PINK_VISIBILITY_ERROR"),
                    err
                );
            });
        }
    }

    @action.bound
    async toggleHandheldVisibilityStatus(value) {
        const oldValue = this.podcast.isVisibleInHandheldApp;
        this.podcast.isVisibleInHandheldApp = value;
        try {
            await this.routeStore.updateHandheldVisibilityStatus(this.podcast.id, this.podcast.isVisibleInHandheldApp);
        } catch (err) {
            runInAction(() => {
                this.podcast.isVisibleInHandheldApp = oldValue;
                this.rootStore.notificationStore.error(
                    localizationService.t("ADMINISTRATION.PODCAST.PREVIEW.HANDHELD_VISIBILITY_ERROR"),
                    err
                );
            });
        }
    }

    @action.bound
    async toggleIsFemaleStatus(value) {
        const oldValue = this.podcast.isFemale;
        this.podcast.isFemale = value;
        try {
            await this.routeStore.updateIsFemaleStatus(this.podcast.id, this.podcast.isFemale);
            if (value) {
                // The server switches off the toggles female content locks; show them the same way.
                runInAction(() => {
                    femaleContentLockedFields.podcast.forEach((field) => (this.podcast[field] = false));
                });
            }
        } catch (err) {
            runInAction(() => {
                this.podcast.isFemale = oldValue;
                this.rootStore.notificationStore.error(
                    localizationService.t("ADMINISTRATION.PODCAST.PREVIEW.IS_FEMALE_ERROR"),
                    err
                );
            });
        }
    }

    @action.bound
    async toggleHlsEnabledStatus(value) {
        const oldValue = this.podcast.podcastFeed.hlsEnabled;
        this.podcast.podcastFeed.hlsEnabled = value;
        try {
            await this.routeStore.updateHlsEnabledStatus(this.podcast.id, this.podcast.podcastFeed.hlsEnabled);
        } catch (err) {
            runInAction(() => {
                this.podcast.podcastFeed.hlsEnabled = oldValue;
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Unable to change HLS enabled status.",
                    err
                );
            });
        }
    }

    @action.bound
    async setScheduledRelease(value) {
        const utcValue = value ? new Date(value).toISOString() : null;
        const oldValue = this.podcast.scheduledReleaseAt;
        this.podcast.scheduledReleaseAt = utcValue;
        try {
            await this.routeStore.updatePodcastScheduledReleaseAt(this.podcast.id, utcValue);
        } catch (err) {
            runInAction(() => {
                this.podcast.scheduledReleaseAt = oldValue;
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Unable to update scheduled release.",
                    err
                );
            });
        }
    }

    onClickEdit = () => {
        this.rootStore.routerStore.goTo(
            "master.administration.podcast.edit-group",
            { id: this.podcast.parentPodcastId },
            { subPodcastId: this.podcast.id }
        );
    };

    onClickPodcastEpisodeList = () => {
        this.rootStore.routerStore.goTo("master.administration.podcast.episodes", {
            id: this.podcast.id,
        });
    };

    setPanelRef = (ref) => {
        this.panelRef = ref;
    };

    onDestroy() {
        this.subPodcastAnalyticsStatsWidgetViewStore.destroy();
        super.onDestroy();
    }
}

export default PodcastGroupPreviewSubPodcastViewStore;
